// ProfileDropdown - Kullanici profil dropdown menu
// Profilim, Agentlarim, Cuzdan, Ayarlar, Cikis Yap
// Click outside to close + keyboard navigation
import { Layout, LogOut, MessageSquare, Settings, Shield, User as UserIcon, Wallet } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import type { User } from '../../types'

interface ProfileDropdownProps {
  user: User | null
}

export default function ProfileDropdown({ user }: ProfileDropdownProps) {
  const { logout } = useAuth()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen])

  const handleLogout = () => {
    logout()
    // redirect to /login is handled in AuthContext logout()
  }

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      {/* Avatar Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-label="User menu"
        aria-expanded={isOpen}
        aria-haspopup="true"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '6px 12px',
          borderRadius: 6,
          border: '1px solid var(--border-default)',
          background: 'var(--card-bg)',
          color: 'var(--text-primary)',
          cursor: 'pointer',
          transition: 'all .2s',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--hover-bg)')}
        onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--card-bg)')}
      >
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #2563eb, #7c3aed)',
            color: '#fff',
            fontSize: 12,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {(user?.display_name ?? user?.username ?? 'U').charAt(0).toUpperCase()}
        </div>
        <span className="desktop-only" style={{ fontSize: 13, fontWeight: 700, letterSpacing: '-0.2px' }}>
          {user?.display_name || user?.username || 'User'}
        </span>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            right: 0,
            top: 44,
            background: 'var(--card-bg)',
            border: '1px solid var(--border-default)',
            borderRadius: 10,
            minWidth: 200,
            zIndex: 1000,
            boxShadow: '0 8px 32px rgba(0,0,0,.5)',
            padding: '6px 0',
          }}
        >
          {/* Menu Items */}
          {user?.id && (
            <Link
              to={`/profile/${user?.username ?? user?.id}`}
              role="menuitem"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 16px',
                color: 'var(--text-secondary)',
                fontSize: 13,
                textDecoration: 'none',
                transition: 'all .15s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--hover-bg)'
                e.currentTarget.style.color = 'var(--text-primary)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent'
                e.currentTarget.style.color = 'var(--text-secondary)'
              }}
            >
              <UserIcon size={16} />
              Profile
            </Link>
          )}

          <Link
            to="/"
            role="menuitem"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 16px',
              color: 'var(--text-secondary)',
              fontSize: 13,
              textDecoration: 'none',
              transition: 'all .15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--hover-bg)'
              e.currentTarget.style.color = 'var(--text-primary)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent'
              e.currentTarget.style.color = 'var(--text-secondary)'
            }}
          >
            <Layout size={16} />
            Landing Page
          </Link>

          <Link
            to="/my-agents"
            role="menuitem"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 16px',
              color: 'var(--text-secondary)',
              fontSize: 13,
              textDecoration: 'none',
              transition: 'all .15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--hover-bg)'
              e.currentTarget.style.color = 'var(--text-primary)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent'
              e.currentTarget.style.color = 'var(--text-secondary)'
            }}
          >
            <UserIcon size={16} />
            My Agents
          </Link>

          <Link
            to="/wallet"
            role="menuitem"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 16px',
              color: 'var(--text-secondary)',
              fontSize: 13,
              textDecoration: 'none',
              transition: 'all .15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--hover-bg)'
              e.currentTarget.style.color = 'var(--text-primary)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent'
              e.currentTarget.style.color = 'var(--text-secondary)'
            }}
          >
            <Wallet size={16} />
            Wallet (Cüzdan)
          </Link>

          <Link
            to="/ask-ai"
            role="menuitem"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 16px',
              color: 'var(--text-secondary)',
              fontSize: 13,
              textDecoration: 'none',
              transition: 'all .15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--hover-bg)'
              e.currentTarget.style.color = 'var(--text-primary)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent'
              e.currentTarget.style.color = 'var(--text-secondary)'
            }}
          >
            <MessageSquare size={16} />
            AskAI / Support
          </Link>

          {/* Dashboard v2 — RunAI OS */}
          <div style={{ borderTop: '1px solid var(--border-default)', margin: '4px 0' }} />
          <Link
            to="/dashboard"
            role="menuitem"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 16px',
              color: '#6366f1',
              fontSize: 13,
              textDecoration: 'none',
              fontWeight: 600,
              transition: 'all .15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(99,102,241,0.08)' }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
          >
            <Settings size={16} color="#6366f1" />
            ⚡ RunAI OS Dashboard
          </Link>

          {/* Classic Dashboard */}
          <Link
            to="/aicloud/app/dashboard"
            role="menuitem"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 16px',
              color: 'var(--text-secondary)',
              fontSize: 13,
              textDecoration: 'none',
              transition: 'all .15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--hover-bg)'
              e.currentTarget.style.color = 'var(--text-primary)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent'
              e.currentTarget.style.color = 'var(--text-secondary)'
            }}
          >
            <Layout size={16} />
            Classic Dashboard
          </Link>

          {(user?.role === 'admin' || user?.role === 'both') && (
            <Link
              to="/admin"
              role="menuitem"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 16px',
                color: '#ef4444',
                fontSize: 13,
                textDecoration: 'none',
                fontWeight: 700,
                transition: 'all .15s',
                background: 'rgba(239, 68, 68, 0.05)'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)' }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(239, 68, 68, 0.05)' }}
            >
              <Shield size={16} color="#ef4444" />
              Admin Panel
            </Link>
          )}
          <div style={{ borderTop: '1px solid var(--border-default)', margin: '4px 0' }} />

          <Link
            to="/settings"
            role="menuitem"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 16px',
              color: 'var(--text-secondary)',
              fontSize: 13,
              textDecoration: 'none',
              transition: 'all .15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--hover-bg)'
              e.currentTarget.style.color = 'var(--text-primary)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent'
              e.currentTarget.style.color = 'var(--text-secondary)'
            }}
          >
            <Settings size={16} />
            Settings (Ayarlar)
          </Link>

          {/* Logout */}
          <div style={{ borderTop: '1px solid var(--border-default)', marginTop: 4, paddingTop: 4 }}>
            <button
              onClick={() => void handleLogout()}
              role="menuitem"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                width: '100%',
                padding: '10px 16px',
                color: '#f85149',
                fontSize: 13,
                background: 'none',
                border: 'none',
                textAlign: 'left',
                cursor: 'pointer',
                transition: 'background .15s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--hover-bg)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <LogOut size={16} />
              Logout (Çıkış)
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
